import React from 'react';
import { Alert, Button } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import {
  classifyTassoQuintoError,
  TassoQuintoErrorKind,
} from '../../../services/api/tasso-quinto';
import {
  MANAGED_LEAD_AUTH_UNAVAILABLE_READ_MESSAGE,
  MANAGED_LEAD_OWNER_MISSING_MESSAGE,
  MANAGED_LEAD_SESSION_EXPIRED_MESSAGE,
} from '../managedLeadFailures';

interface TassoQuintoErrorAlertProps {
  error: unknown;
  retry: () => void;
  retryableMessage: string;
  unauthorizedMessage: string;
  notFoundMessage: string;
  /**
   * An operator with no HubSpot owner has no Deal to be shown, which is not a
   * breakage: pages that can say so in their own words do, and the rest fall
   * back to the shared sentence.
   */
  ownerMissingMessage?: string;
}

/**
 * The single translation from a Tasso Quinto read failure to what the operator
 * sees. Every managed lead page renders its errors through this, so the lists,
 * the detail, the Notes and the administrative queue cannot end up disagreeing
 * about what an expired session or a refused read looks like.
 *
 * Only two branches offer a way forward, and they are mutually exclusive: an
 * expired session can lead nowhere but the login, and a retryable failure is
 * the only one Riprova can do anything about.
 */
const TassoQuintoErrorAlert = ({
  error,
  retry,
  retryableMessage,
  unauthorizedMessage,
  notFoundMessage,
  ownerMissingMessage = MANAGED_LEAD_OWNER_MISSING_MESSAGE,
}: TassoQuintoErrorAlertProps) => {
  const errorKind = classifyTassoQuintoError(error);

  if (errorKind === 'session_expired') {
    return (
      <Alert
        severity="warning"
        action={
          <Button color="inherit" component={RouterLink} to="/login">
            Vai al login
          </Button>
        }
      >
        {MANAGED_LEAD_SESSION_EXPIRED_MESSAGE}
      </Alert>
    );
  }

  if (errorKind === 'unauthorized') {
    return <Alert severity="error">{unauthorizedMessage}</Alert>;
  }

  if (errorKind === 'not_found') {
    return <Alert severity="info">{notFoundMessage}</Alert>;
  }

  // Not an error of this page: there is simply nothing assigned to the
  // operator, and the session stays exactly as valid as it was.
  if (errorKind === 'owner_missing') {
    return <Alert severity="info">{ownerMissingMessage}</Alert>;
  }

  const retryableMessages: Partial<Record<TassoQuintoErrorKind, string>> = {
    auth_unavailable: MANAGED_LEAD_AUTH_UNAVAILABLE_READ_MESSAGE,
  };

  return (
    <Alert
      severity="error"
      action={
        <Button color="inherit" onClick={retry}>
          Riprova
        </Button>
      }
    >
      {retryableMessages[errorKind] ?? retryableMessage}
    </Alert>
  );
};

export default TassoQuintoErrorAlert;
